import { generateUniqueIdForArrayElement } from "./string.js";
import { convertRruleByWeekdayToDayOfWeek } from "./schedule.js";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js";
import timezone from "dayjs/plugin/timezone.js";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore.js";
import minMax from "dayjs/plugin/minMax.js";
import { getIsoDate } from "./date.js";
import { RRule, rrulestr } from "../rrule/index.js";
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrBefore);
dayjs.extend(minMax);
const getDefaultOpeningHours = (byweekdays) => {
    const openingHours = [];
    byweekdays.forEach((weekday) => {
        const { dayOfWeek, dayOfWeekIso } = convertRruleByWeekdayToDayOfWeek(weekday);
        if (dayOfWeek && dayOfWeekIso) {
            const id = generateUniqueIdForArrayElement(openingHours, {
                prefix: "new-opening-hour-",
            });
            const data = {
                id,
                dayOfWeek,
                dayOfWeekIso,
                opens: "08:00",
                closes: "17:00",
            };
            openingHours.push(data);
        }
    });
    return openingHours;
};
const FIXED_DATE = "2000-01-01";
const mergeOpeningHours = (openingHours) => {
    if (!openingHours || openingHours.length === 0) {
        return [];
    }
    // Parse time strings into dayjs objects, filter invalid slots, and sort by opening time.
    const dayjsSlots = openingHours
        .filter((slot) => slot.opens && slot.closes)
        .map((slot) => ({
        ...slot,
        opens: dayjs.utc(`${FIXED_DATE} ${slot.opens}`),
        closes: dayjs.utc(`${FIXED_DATE} ${slot.closes}`),
    }))
        .sort((a, b) => (a.opens.isBefore(b.opens) ? -1 : 1));
    if (dayjsSlots.length === 0) {
        return [];
    }
    const merged = [];
    let current = { ...dayjsSlots[0] };
    for (let i = 1; i < dayjsSlots.length; i += 1) {
        const next = dayjsSlots[i];
        // Overlap/Abutment: If the next slot opens before or exactly at the current slot's close time.
        if (next.opens.isSameOrBefore(current.closes)) {
            // Merge by extending the current slot's closing time if the next slot closes later.
            // dayjs.max() ensures the merged slot covers the maximum duration.
            current.closes = dayjs.max(next.closes, current.closes);
        }
        else {
            // No overlap or abutment. The current slot is finalized.
            merged.push(current);
            // Start a new merged slot with the current next slot.
            current = { ...next };
        }
    }
    // Push the final merged slot (which is stored in 'current').
    merged.push(current);
    // Convert the dayjs objects back to 'HH:mm' format for the final result.
    return merged.map((slot) => ({
        ...slot,
        opens: slot.opens.format("HH:mm"),
        closes: slot.closes.format("HH:mm"),
    }));
};
const findOverlaps = (slotsA, slotsB) => {
    const overlaps = [];
    for (const slotA of slotsA) {
        // Parse times into comparable dayjs objects
        const aStart = dayjs.utc(`${FIXED_DATE} ${slotA.opens}`);
        const aEnd = dayjs.utc(`${FIXED_DATE} ${slotA.closes}`);
        for (const slotB of slotsB) {
            // Parse times into comparable dayjs objects
            const bStart = dayjs.utc(`${FIXED_DATE} ${slotB.opens}`);
            const bEnd = dayjs.utc(`${FIXED_DATE} ${slotB.closes}`);
            // 1. Overlap Start: The later of the two opening times
            const overlapStart = dayjs.max(aStart, bStart);
            // 2. Overlap End: The earlier of the two closing times
            const overlapEnd = dayjs.min(aEnd, bEnd);
            // 3. Check if a valid overlap exists (start must be strictly before end)
            if (overlapStart.isBefore(overlapEnd)) {
                overlaps.push({
                    // Format the dayjs object back to HH:mm string
                    opens: overlapStart.format("HH:mm"),
                    closes: overlapEnd.format("HH:mm"),
                });
            }
        }
    }
    return mergeOpeningHours(overlaps);
};
const getAvailableOpeningHoursPerDate = (start, end, schedules) => {
    const mergedOpeningHoursPerDate = {};
    for (const schedule of schedules) {
        const { rrule: rruleString, timeZone } = schedule;
        if (rruleString && timeZone) {
            const rrule = rrulestr(rruleString);
            // rrule with start and end/until (if given) shifted to UTC
            const modifiedRrule = new RRule({
                ...rrule.origOptions,
                dtstart: rrule.origOptions.dtstart
                    ? dayjs(rrule.origOptions.dtstart).tz(timeZone).utc(true).toDate()
                    : dayjs().startOf("day").utc(true).toDate(),
                until: rrule.origOptions.until
                    ? dayjs(rrule.origOptions.until).tz(timeZone).utc(true).toDate()
                    : undefined,
            });
            // get occurrences (instances of repeating events) within range
            const occurrences = modifiedRrule.between(start.toDate(), end.toDate(), true);
            for (const occurrence of occurrences) {
                const occurrenceUtc = dayjs.utc(occurrence);
                const date = getIsoDate(occurrenceUtc);
                const openingHours = schedule.OpeningHours?.filter((oh) => oh.dayOfWeek === occurrenceUtc.day()) ?? [];
                // merge opening hours with previous ones if any
                const previous = mergedOpeningHoursPerDate[date] ?? [];
                const mergedOpeningHours = mergeOpeningHours([
                    ...openingHours,
                    ...previous,
                ]);
                // set the merged opening hours
                mergedOpeningHoursPerDate[date] = mergedOpeningHours.map((moh) => ({
                    opens: moh.opens,
                    closes: moh.closes,
                }));
            }
        }
    }
    return mergedOpeningHoursPerDate;
};
export { getDefaultOpeningHours, mergeOpeningHours, findOverlaps, getAvailableOpeningHoursPerDate, };
//# sourceMappingURL=openingHour.js.map